import { observer } from "mobx-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FONT_COLOR, FONT_VARIANT } from "../../../../components/fonts/font-props";
import { FormFieldLine } from "../../../../components/form-field-line/form-field-line";
import FormFieldLineLayout from "../../../../components/layouts/form-field-line-layout/form-field-line-layout";
import { Input } from "../../../../components/inputs/input/input";
import usePage from "../../../../components/layouts/base-page-layout/page-hook";
import EditorPageLayout from "../../../../components/layouts/editor-page/editor-page-layout";
import { ConfirmModal } from "../../../../components/modal/components/confirm-modal/confirm-modal";
import useNavigationBlocker from "../../../../hooks/navigation-blocker-hook";
import useValidationGroup from "../../../../hooks/validation-group/validation-group-hook";
import useTranslation from "../../../../translation/hooks/translation-hook";
import TranslationInput from "../../../../translation/translation-input/translation-input";
import TranslationTextArea from "../../../../translation/translation-textarea/translation-textarea";
import resolutionListStore from "../../stores/resolution-list-store";
import ResolutionTypeViewer from "./resolution-type-viewer/resolution-type-viewer";
import Card from "../../../../components/layouts/card/card";
import FontBody1 from "../../../../components/fonts/font-body1/font-body1";
import { exchangeresolutionTypeList, EXCHANGE_RESOLUTION_MAP, RESOLUTION_ID, EXCHNAGE_RESOLUTION_TYPE } from "../../constants";
import { SelectDropdown } from "../../../../components/inputs/select-dropdown/select-dropdown";
import dashboardSettingsStore from "../../../../stores/dashboard-settings-store";
import { PROFILE_CAPABILITIES } from "../../../../models/ProfileCapabilities";
import { getResolutionDisplayFieldVisibility } from "../../utils/resolution-display-translation";

const ResolutionEditor: React.FC = observer(() => {
    const navigate = useNavigate();
    const params = useParams();
    const { setSubTitles, setInstruction } = usePage();
    const vGroup = useValidationGroup();
    const {
        hasChanges: hasTranslationChanges,
        save: saveTranslations, isSaving: isSavingTranslations,
        discardChanges: translationsDiscardChanges, setTranslatableKeys
    } = useTranslation();

    const [isShowUnsaved, setIsShowUnsaved] = useState(false);

    const resolutionId = params.resolutionId as string | number;
    const resolution = resolutionListStore.selectedResolution;

    useEffect(() => {
        resolutionListStore.setSelectedResolutionId(resolutionId);
    }, [resolutionId]);

    useEffect(() => {
        setSubTitles(['Resolutions', resolution?.internalName]);
        setInstruction(undefined);
    }, [setSubTitles, setInstruction, resolution?.internalName]);

    const keys = resolution ? resolution?.getTranslationKeys() : null;
    useEffect(() => {
        if (keys) {
            setTranslatableKeys(keys);
        }
        return () => setTranslatableKeys([]);
    }, [setTranslatableKeys, keys]);

    const hasChanges = (): boolean => {
        return resolution?.hasChanges() || hasTranslationChanges();
    }

    const discardChanges = () => {
        resolution?.discardChanges();
        translationsDiscardChanges();
    }

    const { retryNavigation, unblockNavigation } = useNavigationBlocker(
        () => hasChanges(),
        useCallback(() => setIsShowUnsaved(true), [setIsShowUnsaved])
    );

    const onSaveClick = () => {
        const promised = [];

        if (resolution?.hasChanges()) {
            promised.push(
                resolution?.save()
            );
        }
        if (hasTranslationChanges()) {
            promised.push(
                saveTranslations()
            );
        }

        Promise.all(promised).then(() => {
            unblockNavigation();
            navigate({
                pathname: `/resolutions/${resolution?.id}`,
            }, { replace: true });
        })
    }

    const isExchangeType = [
        RESOLUTION_ID.INSTANT_VARIANT_EXCHANGE,
        RESOLUTION_ID.PRODUCT_EXCHANGE,
        RESOLUTION_ID.MANUAL_EXCHANGE,
    ].includes(resolution?.resolutionId as RESOLUTION_ID);


    /** Field visibility based on resolution type and capability */
    const resolveFieldVisibility = () => {
        const manualExchangeCapability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.MANUAL_EXCHANGE);
        const productExchangeCapability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.PRODUCT_EXCHANGE);
        const displayFieldVisibility = getResolutionDisplayFieldVisibility(
            resolution?.resolutionId as RESOLUTION_ID
        );

        switch (resolution?.resolutionId) {
            case RESOLUTION_ID.MANUAL_EXCHANGE:
                return {
                    showExchangeType: manualExchangeCapability?.isExchangeTypeSupported !== false,
                    showInternalName: manualExchangeCapability?.isInternalNameSupported !== false,
                    ...displayFieldVisibility,
                };

            case RESOLUTION_ID.PRODUCT_EXCHANGE:
                return {
                    showExchangeType: productExchangeCapability?.isExchangeTypeSupported !== false,
                    showInternalName: productExchangeCapability?.isInternalNameSupported !== false,
                    ...displayFieldVisibility,
                };

            default:
                return {
                    showExchangeType: true,
                    showInternalName: true,
                    ...displayFieldVisibility,
                };
        }
    };

    const fieldVisibility = resolveFieldVisibility();

    return (
        <div className="resolution-editor">
            <EditorPageLayout
                toolbar={{
                    saveButton: {
                        hasChanges: hasChanges(),
                        onClick: () => onSaveClick(),
                        disabled: !vGroup.isValid() || !hasChanges() || isSavingTranslations(),
                        isValid: vGroup.isValid(),
                    },
                    isLoading: resolutionListStore.isLoading || resolution?.isSaving()
                }}
            >
                <Card
                    toolbar={{
                        title: <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>Details</FontBody1>,
                    }}
                >
                    <FormFieldLineLayout>
                        {isExchangeType && fieldVisibility.showExchangeType &&
                            <FormFieldLine
                                label="Exchange Type"
                                note="This is the type of resolution, and cannot be changed."
                            >
                                <SelectDropdown
                                    value={EXCHANGE_RESOLUTION_MAP[resolution?.resolutionId as unknown as EXCHNAGE_RESOLUTION_TYPE]}
                                    options={exchangeresolutionTypeList()}
                                    disabled
                                />
                            </FormFieldLine>
                        }

                        {fieldVisibility.showInternalName &&
                            <FormFieldLine
                                label="Internal Name"
                                note="This is displayed to you in the list of resolutions on Resolutions Page."
                            >
                                <Input
                                    value={resolution?.internalName}
                                    onChange={(v) => resolution?.setInternalName(v as string)}
                                    required
                                    validationGroup={vGroup}
                                />
                            </FormFieldLine>
                        }

                        {fieldVisibility.showDisplayName &&
                            <FormFieldLine
                                label="Display Name"
                                note="This is displayed to your customers on the select Resolution Page."
                            >
                                <TranslationInput translationKey={resolution?.name} required={fieldVisibility.showDisplayName} validationGroup={vGroup} />
                            </FormFieldLine>
                        }

                        {fieldVisibility.showDisplayDescription &&
                            <FormFieldLine
                                label="Display Description"
                                note="This description is displayed to customers on the Select Resolution Page."
                            >
                                <TranslationTextArea
                                    translationKey={resolution?.description}
                                    required={fieldVisibility.showDisplayDescription}
                                    maxLength={200}
                                    showCharactersCount={true}
                                    validationGroup={vGroup} />
                            </FormFieldLine>
                        }
                    </FormFieldLineLayout>
                </Card>

                <ResolutionTypeViewer resolution={resolution} validationGroup={vGroup} />
            </EditorPageLayout>

            {isShowUnsaved &&
                <ConfirmModal
                    cancelButton={{
                        title: 'Cancel',
                        onClick: () => setIsShowUnsaved(false)
                    }}
                    okButton={{
                        title: 'Discard',
                        onClick: () => {
                            discardChanges();
                            setIsShowUnsaved(false);
                            retryNavigation();
                        }
                    }}
                />
            }
        </div>
    );
});

export default ResolutionEditor;
